<template>
  <div>
    <loading-screen v-if="isLoading" />
    <uploaded-rules
      v-if="hasUploadedFile"
      :file-name="fileName"
      :workbook="workbook"
      @finished="finishedUploading"
    />
    <div
      v-else
      class="p-6 text-gray-900 bg-white rounded-lg text-left font-medium shadow-lg"
    >
      <h1 class="font-semibold text-xl">
        <fa-icon
          :icon="['fas', 'comment-plus']"
          size="lg"
          class="mr-1 text-blue-400"
        />
        {{ $t("Sentiment rules", "Sentiment rules") }}
      </h1>
      <div class="mx-12 mt-4 p-4 text-center">
        <sentiment-rule-filters v-model="filters" />
        <div class="text-left text-sm mb-2 ml-1 font-normal flex items-center">
          <div>
            <b>{{ (rules.length > filteredRules.length ? filteredRules.length + ' / ' : '') }} {{ rules.length }}</b> {{ $t("Sentiment rules", "Sentiment rules") }}
          </div>
          <z-button
            :text="$t('Export xlsx', 'Export xlsx')"
            @click="exportXlsx()"
          />
        </div>
        <progress-bar
          v-if="isDeleting"
          :all="deleteCount"
          :completed="deleteFinishedCount"
          class="mt-4"
        />
        <div
          v-if="selectedRules.length === 0"
          class="flex"
        >
          <div
            class="w-full flex border border-gray-400 bg-gray-200 font-normal text-sm py-2 text-left"
          >
            <div class="w-1/12">
              <fa-icon
                :icon="['fal', 'circle']"
                size="lg"
                class="text-blue-400 mx-4 cursor-pointer"
                @click.stop="selectAll"
              />
            </div>
            <div
              class="w-1/12 cursor-pointer"
              @click="handleSorting('phrase')"
            >
              <span>
                {{ $t("Phrase", "Phrase") }}
                <fa-icon
                  v-if="sort.column === 'phrase'"
                  class="fa-icon sorting-icon"
                  :icon="['fas', sort.direction === 'asc' ? 'sort-up' : 'sort-down']"
                />
              </span>
            </div>
            <div
              class="w-2/12 ml-1 cursor-pointer"
              @click="handleSorting('value')"
            >
              {{ $t("Sentiment score", "Sentiment score") }}
              <fa-icon
                v-if="sort.column === 'value'"
                class="fa-icon sorting-icon"
                :icon="['fas', sort.direction === 'asc' ? 'sort-up' : 'sort-down']"
              />
            </div>
            <div
              class="w-2/12 ml-1 cursor-pointer"
              @click="handleSorting('lang')"
            >
              {{ $t("Language", "Language") }}
              <fa-icon
                v-if="sort.column === 'lang'"
                class="fa-icon sorting-icon"
                :icon="['fas', sort.direction === 'asc' ? 'sort-up' : 'sort-down']"
              />
            </div>
            <div class="w-1/12 text-center">
              {{ $t("Stemmed", "Stemmed") }}
            </div>
            <div class="w-2/12">
              {{ $t("Projects", "Projects") }}
            </div>
          </div>
        </div>
        <div
          v-else
          class="w-full flex border border-gray-400 bg-gray-200 font-normal p-2 text-left"
        >
          <div class="w-1/12 text-center -ml-2">
            <fa-icon
              :icon="['fas', 'scrubber']"
              size="lg"
              class="mr-1 text-blue-400 cursor-pointer"
              @click.stop="deselectAll"
            />
          </div>
          <div class="w-4/12">
            {{ selectedRules.length }} / {{ rules.length }} {{ $t("selected", "selected") }}
          </div>
          <vue-blob-json-csv
            class="w-4/12"
            file-type="json"
            file-name="sentiment_rules"
            :data="downloadableData"
          >
            <div class="text-blue-500 text-right cursor-pointer">
              {{ $t("Download JSON", "Download JSON") }}
            </div>
          </vue-blob-json-csv>
          <div
            class="w-3/12 text-red-500 text-right pr-2 cursor-pointer"
            @click="deleteSelection()"
          >
            {{ $t("Delete phrases", "Delete phrases") }}
          </div>
        </div>
        <div class="w-full flex font-normal pt-3 text-left">
          <div class="w-2/12 -ml-2 opacity-75 mr-2">
            <z-input
              v-model="phrase"
              class="w-full px-2"
              :placeholder="$t('Phrase example', 'e.g. Amazeballs')"
            />
          </div>
          <div class="w-2/12 opacity-75">
            <input
              v-model="score"
              class="w-11/12 py-2 px-2 bg-gray-200 mb-2 rounded text-gray-700 tracking-wider placeholder-neutral-700"
              type="number"
              min="-3"
              max="3"
              :placeholder="$t('Score example', 'e.g. 3')"
            >
          </div>
          <div class="w-2/12 opacity-75">
            <select
              v-model="lang"
              class="w-full p-2 bg-gray-200 mb-2 rounded text-gray-700 tracking-wider placeholder-neutral-700"
            >
              <option
                v-for="(language, index) in languages"
                :key="index"
                :value="language.nickname"
              >
                {{ language.name }}
              </option>
            </select>
          </div>
          <div class="w-1/12 text-center">
            <input
              v-model="stemmed"
              class=""
              type="checkbox"
            >
          </div>
          <div class="w-3/12 px-1">
            <project-dropdown
              :all-projects="projects"
              :selected-projects="selectedProjects"
              :has-label="false"
              @select="addProject"
              @deselect="removeProject"
            />
          </div>
          <div class="flex w-2/12">
            <z-button
              :text="$t('Add', 'Add')"
              class="mt-0 ml-0"
              @click="addRule()"
            />
            <v-popover
              trigger="hover"
              placement="bottom"
              class="m-0"
            >
              <z-button
                type="secondary"
                :text="$t('Upload xlsx', 'Upload xlsx')"
                class="mt-0 ml-0 upload-button tooltip-target"
                @click="openDialog()"
              />
              <template slot="popover">
                <div class="tooltip-content text-xs">
                  {{ $t('Upload sentiment rule tooltip', 'When uploading an xlsx, please include the following columns: Phrase, Sentiment score, Language, Stemming and Projects. Multiple projects can be defined with a comma, missing project names will be ignored.') }}
                </div>
                <a
                  class="text-blue-400 cursor-pointer text-xs"
                  @click="downloadTemplate"
                >
                  <fa-icon
                    :icon="['fal', 'download']"
                    class="mr-1"
                  />
                  {{ $t('Download template', 'Download template .xlsx') }}
                </a>
              </template>
            </v-popover>
            <input
              ref="file"
              type="file"
              name="file"
              accept=".xls, .xlsx"
              style="display: none;"
              @change="uploadExcel()"
            >
          </div>
        </div>
        <div
          v-for="rule in filteredRules"
          :key="rule.id"
        >
          <rule
            :rule="rule"
            :all-projects="projects"
            :is-selected="selectedRules.indexOf(rule.id) > -1"
            @select-clicked="handleSelection($event, rule.id)"
            @delete="deleteRule($event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { actionNames } from '@/sentiment-rules/store'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faCommentPlus, faScrubber, faSortDown, faSortUp } from '@fortawesome/pro-solid-svg-icons'
import { faCircle, faPlusCircle, faSearch, faUpload, faDownload } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import Rule from './components/Rule'
import UploadedRules from './components/UploadedRules'
import ZInput from '@/global/ui/Input'
import * as XLSX from 'xlsx'
import excelWriter from '@/utils/excelWriter'
import Button from '@/global/ui/Button'
import ProjectDropdown from '@/global/ui/ProjectDropdown/ProjectDropdown'
import { actionNames as projectActionNames } from '@/projects/store'
import PQueue from 'p-queue'
import SentimentRuleFilters from '@/sentiment-rules/components/SentimentRuleFilters'
import ProgressBar from '@/global/ui/ProgressBar'
import LoadingScreen from '@/global/ui/LoadingScreen'

library.add(faCommentPlus, faCircle, faPlusCircle, faScrubber, faSearch, faUpload, faDownload, faSortDown, faSortUp)
export default {
  name: 'SentimentRules',
  components: {
    SentimentRuleFilters,
    'project-dropdown': ProjectDropdown,
    'z-button': Button,
    'fa-icon': FontAwesomeIcon,
    Rule,
    UploadedRules,
    ZInput,
    ProgressBar,
    LoadingScreen
  },
  data () {
    return {
      phrase: '',
      score: '',
      stemmed: true,
      lang: 'en',
      selectedRules: [],
      filters: {
        projectId: null,
        language: null,
        polarity: null,
        searchText: ''
      },
      workbook: {},
      fileName: '',
      hasUploadedFile: false,
      selectedProjects: [],
      sort: {
        direction: '',
        column: null
      },
      isDeleting: false,
      deleteCount: 0,
      deleteFinishedCount: 0,
      isLoading: false
    }
  },
  computed: {
    ...mapState({
      rules: state => state.sentimentRules.rules,
      languages: state => state.sentimentRules.languages,
      projects: state => state.projects.projects
    }),
    downloadableData () {
      return this.rules.filter(r => this.selectedRules.includes(r.id)).map((r) => {
        return {
          phrase: r.phrase,
          value: r.value,
          stemmed: r.stemmed,
          lang: r.lang
        }
      })
    },
    filteredRules () {
      let allRules = [...this.rules]
      if (this.sort.column) {
        if (this.sort.column === 'value') {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          allRules = allRules.sort((a, b) => a[this.sort.column] - b[this.sort.column])
        } else {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          allRules = allRules.sort((a, b) => a[this.sort.column].localeCompare(b[this.sort.column]))
        }
        if (this.sort.direction === 'desc') {
          allRules = allRules.reverse()
        }
      }
      return allRules
        .filter(r => r.phrase.toLowerCase().includes(this.filters.searchText.toLowerCase()))
        .filter(r => !this.filters.projectId || r.projects.find(p => p.id === this.filters.projectId))
        .filter(r => !this.filters.language || r.lang === this.filters.language)
        .filter(r => {
          switch (this.filters.polarity) {
            case 'pos':
              return r.value > 0
            case 'neg':
              return r.value < 0
            case 'neu':
              return r.value === 0
            default:
              return true
          }
        })
    }
  },
  async mounted () {
    this.isLoading = true
    await this.$store.dispatch(actionNames.INIT_RULES)
    await this.$store.dispatch(projectActionNames.INIT_PROJECTS)
    this.isLoading = false
  },
  methods: {
    async addRule () {
      if (this.phrase === '' || (!this.score && this.score !== 0) || !this.lang) {
        this.$toast.error(this.$t('Please provide a valid rule', 'Please provide a valid rule'))
        return
      }
      try {
        await this.$store.dispatch(actionNames.ADD_RULE, { phrase: this.phrase, score: this.score, stemmed: this.stemmed, lang: this.lang, projects: this.selectedProjects.map(sp => sp.id) })
        this.phrase = ''
        this.score = ''
        this.selectedProjects = []
      } catch (e) {
        if (e.request && e.request.status === 409) {
          this.$toast.error(this.$t('This rule is already added', 'This rule is already added'))
        } else {
          this.$toast.error(this.$t('Something went wrong', 'Something went wrong'))
        }
      }
    },
    selectAll () {
      this.selectedRules = this.filteredRules.map(r => r.id)
    },
    deselectAll () {
      this.selectedRules = []
    },
    handleSelection (isSelected, id) {
      if (isSelected) {
        this.selectedRules = [...this.selectedRules, id]
      } else {
        this.selectedRules = this.selectedRules.filter(r => r !== id)
      }
    },
    async deleteSelection () {
      try {
        const accepted = await this.$swal('warning', {
          title: this.$t('Delete Sentiment Rule', 'Delete Sentiment Rules'),
          text: this.$t('Delete Sentiment Rule Text', 'Do you want to delete selected sentiment rules?'),
          icon: 'error',
          dangerMode: true,
          buttons: true
        })
        if (!accepted) {
          return
        }
        this.deleteCount = this.selectedRules.length
        this.isDeleting = true
        const queue = new PQueue({ concurrency: 4 })
        this.selectedRules.forEach(ruleId => queue.add(async () => {
          await this.$store.dispatch(actionNames.DELETE_RULE, ruleId)
          this.handleSelection(false, ruleId)
          this.deleteFinishedCount++
          if (this.deleteFinishedCount === this.deleteCount) {
            this.isDeleting = false
            this.deleteFinishedCount = 0
            this.deleteCount = 0
          }
        }))
      } catch (e) {
        this.$toast.error(this.$t('Something went wrong', 'Something went wrong'))
      }
    },
    async deleteRule (id) {
      try {
        const accepted = await this.$swal('warning', {
          title: this.$t('Delete Sentiment Rule', 'Delete Sentiment Rule'),
          text: this.$t('Delete Sentiment Rule Text', 'Do you want to delete the sentiment rule?'),
          icon: 'error',
          dangerMode: true,
          buttons: true
        })
        if (!accepted) {
          return
        }
        await this.$store.dispatch(actionNames.DELETE_RULE, id)
      } catch (e) {
        this.$toast.error(this.$t('Something went wrong', 'Something went wrong'))
      }
    },
    openDialog () {
      this.$refs.file.click()
    },
    async uploadExcel () {
      try {
        const file = this.$refs.file.files[0]
        if (file.name.slice(-3) === 'xls' || file.name.slice(-4) === 'xlsx') {
          const reader = new FileReader()
          reader.onload = async (e) => {
            const workbook = XLSX.read(e.target.result, { type: 'binary' })
            for (var i = 0; i < Object.keys(workbook.Sheets).length; i++) {
              this.workbook[Object.keys(workbook.Sheets)[i]] = XLSX.utils.sheet_to_json(workbook.Sheets[Object.keys(workbook.Sheets)[i]], {
                raw: false,
                header: 1,
                blankrows: false
              })
            }
            this.fileName = file.name
            this.hasUploadedFile = true
          }
          reader.readAsBinaryString(file)
        } else {
          this.$toast.error(this.$t('ANALYSIS.DROP_FILE_TYPE_ERROR'))
        }
      } catch (e) {
        this.$toast.error(this.$t('GENERAL.SOMETHING_WENT_WRONG', 'Something went wrong!'))
      }
      this.$refs.file.value = null
    },
    async finishedUploading () {
      await this.$store.dispatch(actionNames.INIT_RULES)
      this.hasUploadedFile = false
      this.fileName = ''
      this.workbook = {}
    },
    addProject (p) {
      this.selectedProjects = [...this.selectedProjects, p]
    },
    removeProject (p) {
      this.selectedProjects = this.selectedProjects.filter(sp => sp.id !== p.id)
    },
    async downloadTemplate () {
      try {
        await excelWriter({
          fileName: 'template_example.xlsx',
          headers: ['Phrase', 'Sentiment score', 'Stemmed', 'Language', 'Projects'],
          data: [['phrase name', '1', '1', 'en', 'project 1, project 2']]
        })
      } catch (e) {
        console.error(e)
        this.$toast.error(this.$t('Something went wrong', 'Something went wrong'))
      }
    },
    async exportXlsx () {
      try {
        const headers = ['Phrase', 'Sentiment score', 'Stemmed', 'Language', 'Projects']
        const data = this.filteredRules.map(r => [r.phrase, r.value, r.stemmed ? 1 : 0, r.lang, r.projects.reduce((prev, p) => prev ? (prev + ', ' + p.name) : p.name, '')])
        await excelWriter({ fileName: 'sentiment_rules_export.xlsx', headers: headers, data: data })
      } catch (e) {
        this.$toast.error(this.$t('Something went wrong', 'Something went wrong'))
      }
    },
    handleSorting (column) {
      if (column === this.sort.column) {
        switch (this.sort.direction) {
          case '':
            this.sort.direction = 'desc'
            break
          case 'desc':
            this.sort.direction = 'asc'
            break
          default:
            this.sort.direction = ''
            this.sort.column = null
            break
        }
      } else {
        this.sort.column = column
        this.sort.direction = 'desc'
      }
    }
  }
}
</script>
<style>
.upload-button {
  margin-bottom: -2px;
}
.popover-arrow {
  display: none;
}
</style>
